import React, { useEffect, useState } from "react";
import { Box, Slider, Typography } from "@mui/material";

function hexToRgb(hex) {
  const clean = hex.replace("#", "");
  const int = Number.parseInt(clean, 16);
  return {
    r: (int >> 16) & 255,
    g: (int >> 8) & 255,
    b: int & 255,
  };
}

function rgbToHex({ r, g, b }) {
  const toHex = (value) => value.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function mixHexColors(startHex, endHex, ratio) {
  const start = hexToRgb(startHex);
  const end = hexToRgb(endHex);
  const clamped = Math.max(0, Math.min(1, ratio));
  return rgbToHex({
    r: Math.round(start.r + (end.r - start.r) * clamped),
    g: Math.round(start.g + (end.g - start.g) * clamped),
    b: Math.round(start.b + (end.b - start.b) * clamped),
  });
}

function getHeatColor(ratio) {
  const lightBlue = "#6ebbfa";
  const mediumBlue = "#42a5f5";
  const darkBlue = "#1565c0";

  if (ratio <= 0.5) {
    return mixHexColors(lightBlue, mediumBlue, ratio / 0.5);
  }
  return mixHexColors(mediumBlue, darkBlue, (ratio - 0.5) / 0.5);
}

function getIntensityLabel(value, min, max) {
  // Discrete tablet mappings
  // 1..4 => Low, Medium, High, Very High
  if (min === 1 && max === 4) {
    if (value <= 1) return "Low";
    if (value === 2) return "Medium";
    if (value === 3) return "High";
    return "Very High";
  }

  // 1..5 => Low, Medium, High, Very High (for 4 and 5)
  if (min === 1 && max === 5) {
    if (value <= 1) return "Low";
    if (value === 2) return "Medium";
    if (value === 3) return "High";
    return "Very High";
  }

  return String(value);
}

const CleaningLevelSlider = ({
  label = "Intensity",
  value = 0,
  onChange = () => {},
  min = 0,
  max = 100,
  numColumns = 10,
  color = "primary",
  formatDisplayValue = null,
  intensityLabels = null,
  valueLabelDisplay = "auto",
}) => {
  const [internalValue, setInternalValue] = useState(value);

  const handleChange = (event, newValue) => {
    setInternalValue(newValue);
    onChange(newValue);
  };

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Calculate the height of each column based on intensity
  const getColumnHeight = (columnIndex) => {
    // Column height increases as we move right
    // Each column's base height is determined by its position
    const baseHeight = ((columnIndex + 1) / numColumns) * 100;

    // For discrete ranges like min=1, max=5 with 5 columns,
    // map each value to an exact number of filled columns.
    const discreteSteps = max - min + 1;
    if (Number.isInteger(internalValue) && discreteSteps === numColumns) {
      const filledColumns = Math.max(
        0,
        Math.min(numColumns, internalValue - min + 1),
      );
      return columnIndex < filledColumns ? baseHeight : 0;
    }

    // Fallback: proportional fill for non-discrete configurations.
    const range = max - min;
    if (range <= 0) {
      return 0;
    }
    const normalized = ((internalValue - min) / range) * numColumns;
    const fullColumns = Math.floor(normalized);
    const fractional = normalized - fullColumns;

    if (columnIndex < fullColumns) {
      return baseHeight;
    }
    if (columnIndex === fullColumns && fullColumns < numColumns) {
      return baseHeight * fractional;
    } else {
      return 0;
    }
  };

  const colorMap = {
    primary: "#1976d2",
    success: "#43a047",
    error: "#d32f2f",
    warning: "#f57c00",
  };

  const valueRatio = max > min ? (internalValue - min) / (max - min) : 0;
  const clampedValueRatio = Math.max(0, Math.min(1, valueRatio));
  const activeColor = color === "heat" ? getHeatColor(clampedValueRatio) : null;
  const columnColor = colorMap[color] || colorMap.primary;
  const displayValue =
    typeof formatDisplayValue === "function"
      ? formatDisplayValue(internalValue, { min, max })
      : intensityLabels && intensityLabels[internalValue] != null
        ? intensityLabels[internalValue]
        : getIntensityLabel(internalValue, min, max);

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {label && <Typography variant="subtitle2">{label}</Typography>}
      </Box>
      <Typography
        variant="body2"
        sx={{
          width: "100%",
          textAlign: "center",
          color: color === "heat" ? activeColor : "inherit",
          fontWeight: 600,
          mb: -1,
        }}
      >
        {displayValue}
      </Typography>

      {/* Column Visualization */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 0.5,
          height: 60,
          mb: 1,
          px: 1,
        }}
      >
        {Array.from({ length: numColumns }).map((_, index) => (
          <Box
            key={index}
            sx={{
              flex: 1,
              height: `${Math.max(getColumnHeight(index), 0)}%`,
              backgroundColor:
                color === "heat"
                  ? getHeatColor((index + 1) / numColumns)
                  : columnColor,
              borderRadius: "2px 2px 0 0",
              transition: "height 0.1s ease",
              opacity: getColumnHeight(index) > 5 ? 1 : 0.3,
            }}
          />
        ))}
      </Box>

      {/* Slider */}
      <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
        <Slider
          value={internalValue}
          onChange={handleChange}
          min={min}
          max={max}
          step={1}
          marks
          valueLabelDisplay={valueLabelDisplay}
          sx={{
            flex: 1,
            color: color === "heat" ? activeColor : undefined,
            "& .MuiSlider-thumb": {
              backgroundColor: color === "heat" ? activeColor : undefined,
            },
            "& .MuiSlider-track": {
              backgroundColor: color === "heat" ? activeColor : undefined,
              border: "none",
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default CleaningLevelSlider;
